<template>
  <div class="results">
    <div class="results-header">
      <h2>🎯 Test Results</h2>
      <p>Results appear as models complete their processing</p>
    </div>

    <div class="results-content">
      <!-- Only show models that have been tested (exist in modelStates) -->
      <div class="results-grid" :class="{ 'side-by-side': testedModels.length === 2 }">
        <div v-for="modelId in testedModels" :key="modelId" class="result-card">
          <div class="result-header">
            <div class="result-model">{{ getModelName(modelId) }}</div>
            <div class="result-status">
              <div v-if="getModelState(modelId).status === 'loading'" class="status-loading">
                <div class="mini-spinner"></div>
                <span>Processing... {{ getElapsedTime(modelId) }}</span>
              </div>
              <div v-else-if="getModelState(modelId).status === 'completed'" class="status-completed">
                <div class="result-cost">{{ getModelCost(modelId) }}</div>
                <div class="duration-badge">{{ formatDuration(getModelState(modelId).duration) }}</div>
              </div>
              <div v-else-if="getModelState(modelId).status === 'error'" class="status-error">
                <span class="error-badge">Error</span>
                <div class="duration-badge">{{ formatDuration(getModelState(modelId).duration) }}</div>
              </div>
            </div>
          </div>

          <div class="result-content">
            <div v-if="getModelState(modelId).status === 'loading'" class="loading-placeholder">
              <div class="loading-animation">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
              <p>{{ getModelName(modelId) }} is generating response...</p>
              <div class="loading-progress">
                <div class="progress-bar" :style="{ width: getProgressWidth(modelId) }"></div>
              </div>
            </div>

            <div v-else-if="getModelState(modelId).status === 'completed'" class="response-content">
              <JsonViewer :data="getModelState(modelId).result" />
            </div>

            <div v-else-if="getModelState(modelId).status === 'error'" class="error-content">
              <div class="error-header">
                <strong>❌ Error Details:</strong>
              </div>
              <JsonViewer :data="{ error: getModelState(modelId).error, timestamp: new Date().toISOString() }" />
            </div>
          </div>
        </div>
      </div>

      <!-- Show info about selected but not tested models -->
      <div v-if="selectedButNotTested.length > 0" class="pending-models">
        <div class="pending-header">
          <h3>📋 Selected Models</h3>
          <p>{{ selectedButNotTested.length }} model{{ selectedButNotTested.length !== 1 ? 's' : '' }} selected but not
            tested yet</p>
        </div>
        <div class="pending-list">
          <div v-for="modelId in selectedButNotTested" :key="'pending-' + modelId" class="pending-model">
            <span class="model-name">{{ getModelName(modelId) }}</span>
            <span class="model-cost">{{ getModelCost(modelId) }}</span>
          </div>
        </div>
        <p class="pending-note">Click "Test Selected Models" to run these models</p>
      </div>

      <!-- <div v-if="completedResults.length > 1" class="recommendation">
        <h3>💡 Recommendation</h3>
        <p>{{ getRecommendation() }}</p>
      </div> -->
    </div>
  </div>
</template>

<script>
import JsonViewer from './JsonViewer.vue'

export default {
  name: 'Results',
  components: {
    JsonViewer
  },
  props: {
    results: {
      type: Array,
      required: true
    },
    models: {
      type: Array,
      required: true
    },
    modelStates: {
      type: Object,
      required: true
    },
    selectedModels: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      elapsedIntervals: {}
    }
  },
  computed: {
    completedResults() {
      return this.results.filter(r => r.success)
    },
    testedModels() {
      // Only show models that actually exist in modelStates (have been tested)
      return Object.keys(this.modelStates)
    },
    selectedButNotTested() {
      // Show models that are selected but haven't been tested yet
      return this.selectedModels.filter(modelId => !this.modelStates[modelId])
    }
  },
  mounted() {
    // Start elapsed time updates for loading models
    this.startElapsedTimeUpdates()
  },
  beforeUnmount() {
    // Clear all intervals
    Object.values(this.elapsedIntervals).forEach(interval => {
      clearInterval(interval)
    })
  },
  methods: {
    getModelName(modelId) {
      const model = this.models.find(m => m.id === modelId)
      return model ? model.name : modelId
    },
    getModelCost(modelId) {
      const state = this.getModelState(modelId)
      
      // If we have actual cost data from the API response, use that
      if (state.costInfo && state.costInfo.total_cost !== undefined) {
        return `$${parseFloat(state.costInfo.total_cost).toFixed(4)}`
      }
      
      // Fallback to estimated cost from model definition
      const model = this.models.find(m => m.id === modelId)
      return model ? model.costPerRequest : 'Unknown'
    },
    getActualCost(modelId) {
      const state = this.getModelState(modelId)
      return state.costInfo || null
    },
    formatCostBreakdown(costInfo) {
      if (!costInfo) return null
      
      return {
        inputTokens: costInfo.input_tokens || 0,
        outputTokens: costInfo.output_tokens || 0,
        totalTokens: costInfo.total_tokens || 0,
        inputCost: parseFloat(costInfo.input_cost || 0).toFixed(4),
        outputCost: parseFloat(costInfo.output_cost || 0).toFixed(4),
        totalCost: parseFloat(costInfo.total_cost || 0).toFixed(4),
        model: costInfo.model || 'unknown',
        pricingTier: costInfo.pricing_tier || 'standard'
      }
    },
    getCostTooltip(modelId) {
      const breakdown = this.formatCostBreakdown(this.getActualCost(modelId))
      if (!breakdown) return 'Estimated cost'
      
      return `Actual cost breakdown:
    Input: ${breakdown.inputTokens} tokens ($${breakdown.inputCost})
    Output: ${breakdown.outputTokens} tokens ($${breakdown.outputCost})
    Model: ${breakdown.model}
    Tier: ${breakdown.pricingTier}`
    },
    getModelState(modelId) {
      return this.modelStates[modelId] || { status: 'loading', startTime: Date.now(), error: null, result: null }
    },
    formatResponse(response) {
      return JSON.stringify(response, null, 2)
    },
    formatDuration(duration) {
      if (!duration) return ''
      const seconds = Math.floor(duration / 1000)
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60

      if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`
      } else {
        return `${seconds}s`
      }
    },
    getElapsedTime(modelId) {
      const state = this.getModelState(modelId)
      if (state.status !== 'loading') return ''

      const elapsed = Date.now() - state.startTime
      return this.formatDuration(elapsed)
    },
    getProgressWidth(modelId) {
      const state = this.getModelState(modelId)
      if (state.status !== 'loading') return '0%'

      const elapsed = Date.now() - state.startTime
      const maxTime = 300000 // 5 minutes expected max
      const progress = Math.min((elapsed / maxTime) * 100, 90) // Cap at 90% until complete
      return `${progress}%`
    },
    startElapsedTimeUpdates() {
      this.selectedModels.forEach(modelId => {
        if (this.getModelState(modelId).status === 'loading') {
          this.elapsedIntervals[modelId] = setInterval(() => {
            this.$forceUpdate() // Force reactivity update for elapsed time
          }, 1000) // Update every second
        }
      })
    },
    getRecommendation() {
      const successfulResults = this.completedResults

      if (successfulResults.length === 0) {
        return "No successful results to compare yet. Please wait for models to complete."
      }

      const hasNano = successfulResults.some(r => r.model === 'gpt-4.1-nano')
      const hasMini = successfulResults.some(r => r.model === 'gpt-4o-mini')
      const hasExpensive = successfulResults.some(r =>
        ['gpt-4.5-preview', 'gpt-4o', 'gpt-4.1'].includes(r.model)
      )

      if (hasNano) {
        return "GPT-4.1 Nano offers the best value - it's the fastest, cheapest, and provides premium quality. Perfect for production use at scale."
      } else if (hasMini && hasExpensive) {
        return "GPT-4o Mini provides excellent value for money. Unless you need specific advanced features, it's more cost-effective than the premium models."
      } else if (hasExpensive) {
        return "Consider if the advanced features justify the higher costs. For most use cases, cheaper alternatives provide similar quality at a fraction of the price."
      }

      return "Compare the responses above to determine which model best fits your needs and budget."
    }
  },
  watch: {
    modelStates: {
      handler(newStates) {
        // Update intervals when model states change
        Object.keys(newStates).forEach(modelId => {
          const state = newStates[modelId]
          if (state.status === 'loading' && !this.elapsedIntervals[modelId]) {
            this.elapsedIntervals[modelId] = setInterval(() => {
              this.$forceUpdate()
            }, 1000)
          } else if (state.status !== 'loading' && this.elapsedIntervals[modelId]) {
            clearInterval(this.elapsedIntervals[modelId])
            delete this.elapsedIntervals[modelId]
          }
        })
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.results {
  background: white;
  margin: 20px;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.results-header {
  background: #f8f9fa;
  padding: 20px 30px;
  border-bottom: 1px solid #e9ecef;
}

.results-header h2 {
  margin-bottom: 5px;
  color: #2c3e50;
}

.results-header p {
  color: #6c757d;
}

.results-content {
  padding: 30px;
}

.results-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.results-grid.side-by-side {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.result-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  border-left: 4px solid #667eea;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.result-model {
  font-size: 1.2em;
  font-weight: 600;
  color: #2c3e50;
}

.result-status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #667eea;
  font-size: 0.9em;
}

.mini-spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

.status-completed {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-error {
  display: flex;
  align-items: center;
  gap: 10px;
}

.error-badge {
  background: #dc3545;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 600;
}

.duration-badge {
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 600;
}

.loading-placeholder {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.loading-animation {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
}

.dot {
  width: 8px;
  height: 8px;
  background: #667eea;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

.dot:nth-child(3) {
  animation-delay: 0s;
}

.loading-progress {
  width: 100%;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 15px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.response-content {
  background: transparent;
  padding: 0;
  border-radius: 8px;
  border: none;
}

.error-content {
  background: transparent;
  padding: 0;
  border-radius: 8px;
}

.error-header {
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #f5c6cb;
  font-weight: 600;
  margin-bottom: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.pending-models {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  text-align: center;
}

.pending-header h3 {
  color: #6c757d;
  margin-bottom: 5px;
}

.pending-header p {
  color: #6c757d;
  font-size: 0.9em;
  margin-bottom: 15px;
}

.pending-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 15px;
}

.pending-model {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9em;
}

.pending-model .model-name {
  font-weight: 600;
  color: #495057;
}

.pending-model .model-cost {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 600;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .results {
    margin: 10px;
  }

  .results-content {
    padding: 20px;
  }

  .results-grid.side-by-side {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .result-card {
    padding: 20px;
  }

  .result-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .result-status {
    flex-wrap: wrap;
  }

  .loading-placeholder {
    padding: 20px 10px;
  }

  .pending-list {
    flex-direction: column;
    align-items: center;
  }

  .pending-model {
    width: 100%;
    max-width: 300px;
    justify-content: space-between;
  }
}

/* Tablet breakpoint */
@media (max-width: 1024px) and (min-width: 769px) {
  .results-grid.side-by-side {
    gap: 15px;
  }

  .result-card {
    padding: 20px;
  }
}

.recommendation {
  background: linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%);
  color: #2d3436;
  padding: 20px;
  border-radius: 12px;
  margin-top: 20px;
  border-left: 4px solid #fdcb6e;
}

.recommendation h3 {
  margin-bottom: 10px;
  color: #2d3436;
}

.result-cost {
  background: #667eea;
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.9em;
  font-weight: 600;
}
</style>